import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	ElementRef,
	HostListener,
	inject,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges
} from "@angular/core";
import { BpModalHeaderComponent } from "@modules/core/components/bp-modal-header/bp-modalheader.component";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { OrdersEntryService } from "@modules/limit-orders/services/orders-entry.service";
import { LimitOrdersService } from "@modules/limit-orders/services/limit-orders.service";
import { ModalService } from "@core-services/modal.service";
import { Helper } from "@helpers/functions";
import {
	LIMIT_ORDER_INFO_TOOLTIP_COUNTER,
	LIMIT_ORDERS_SWITCH_VIEWED,
	PRICE_ORDER_INFO_COOKIE_KEY,
	PRICE_ORDER_INFO_COOKIE_KEY_SELL
} from "@helpers/constants";
import { BehaviorSubject, combineLatest } from "rxjs";
import { filter, take } from "rxjs/operators";
import { Offer, Price } from "@models";
import { LimitOrderQueryParamName } from "@modules/limit-orders/interfaces/limit-orders-route.interface";
import { OrderOption } from "@modules/+new/shared/universal-modal/components/_headers/order-variation/order-header.component";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { OrderType } from "@gql/offer";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
	IOrderSwitchVisibilityType,
	ISwitchSeenCookie
} from "@modules/limit-orders/interfaces/limit-order.view.interface";

@Component({
	selector: "legacy-order-header",
	templateUrl: "./legacy-order-header.component.html",
	styleUrls: ["./legacy-order-header.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class LegacyOrderHeaderComponent extends BpModalHeaderComponent implements OnInit, OnChanges {
	private readonly tradableService = inject(TradableListService);
	private readonly orderEntryService = inject(OrdersEntryService);
	private readonly limitOrdersService = inject(LimitOrdersService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly cdr = inject(ChangeDetectorRef);

	public constructor(protected modalService: ModalService) {
		super(modalService);
	}

	public canShowExtension: boolean = false;

	@Input()
	offer: Offer;

	@Input()
	cameFromHeaderSwitch: boolean = false;

	@Input()
	public priceSubject$: BehaviorSubject<Price> = new BehaviorSubject<Price>(null);

	public orderTypeOptions: OrderOption[] = [];
	@Input()
	public selectedOption: number = 0;
	public selected?: OrderOption;
	extendOptions: OrderOption[];
	public showExtendOptions: boolean = false;

	public sellInfoCookie = 0;
	public buyInfoCookie = 0;
	public hasSeenSellTooltip = false;
	public hasSeenBuyTooltip = false;

	public showInfoText: boolean = false;
	public showNewText: boolean = false;
	private readonly elementRef = inject(ElementRef);

	public readonly isMaxOrdersReached$ = this.limitOrdersService.isMaxOrdersReached$();
	private switchVariations$ = this.limitOrdersService.switchVariationVisibility$;
	private isAssetEligibleForLimitOrder = true;

	public ngOnInit(): void {
		// close modal listener in amount modal
		this.amountModalCloseListener();

		const orderType = this.offer.getType();
		const isSell = orderType === TRADE_TYPE.SELL;
		this.setCookieData(isSell);
	}

	// close modal listener in amount modal
	private amountModalCloseListener(): void {
		combineLatest({ price: this.priceSubject$, onClose: this.modalService.getCurrentModal().onDismiss })
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				filter(({ price, onClose }) => !!price && !!onClose)
			)
			.subscribe(() => {
				this.limitOrdersService.navigateAwayAndUpdateSwitchVisilibityCookie(
					false,
					this.isAssetEligibleForLimitOrder
				);
			});
	}

	private autoOpenSwitcherHelper(price: Price, type: IOrderSwitchVisibilityType): void {
		if (!price) {
			return;
		}

		const getSwitchVisibilityCookie: ISwitchSeenCookie = Helper.getCookie(
			LIMIT_ORDERS_SWITCH_VIEWED,
			true
		) as ISwitchSeenCookie | null;

		switch (type) {
			// Show extension always
			case IOrderSwitchVisibilityType.Show_Always:
				if (getSwitchVisibilityCookie !== ISwitchSeenCookie.NotSeen) {
					this.openSwitch();
				}

				break;
			// Show extension once
			case IOrderSwitchVisibilityType.Show_Once:
				if (getSwitchVisibilityCookie !== ISwitchSeenCookie.Seen) {
					this.openSwitch();
				}

				break;
			// Don't show extension
			case IOrderSwitchVisibilityType.Hidden:
			default:
				break;
		}
	}

	private setCookieData(isSell: boolean): void {
		this.sellInfoCookie =
			+Helper.getCookie(PRICE_ORDER_INFO_COOKIE_KEY_SELL, true) > 0
				? +Helper.getCookie(PRICE_ORDER_INFO_COOKIE_KEY_SELL, true)
				: 0;
		this.buyInfoCookie =
			+Helper.getCookie(PRICE_ORDER_INFO_COOKIE_KEY, true) > 0
				? +Helper.getCookie(PRICE_ORDER_INFO_COOKIE_KEY, true)
				: 0;
		this.showInfoText = !this.cameFromHeaderSwitch && (isSell ? !this.hasSeenSellTooltip : !this.hasSeenBuyTooltip);
		this.hasSeenSellTooltip = this.sellInfoCookie > LIMIT_ORDER_INFO_TOOLTIP_COUNTER;
		this.hasSeenBuyTooltip = this.buyInfoCookie > LIMIT_ORDER_INFO_TOOLTIP_COUNTER;
	}

	private openSwitch(): void {
		this.showExtendOptions = true;
		this.showInfoText = false;
		this.cdr.markForCheck();
	}

	private setCookie(isSell: boolean): void {
		if (!this.cameFromHeaderSwitch) {
			if (isSell) {
				Helper.setCookie(PRICE_ORDER_INFO_COOKIE_KEY_SELL, (this.sellInfoCookie + 1).toString(), true);
			} else {
				Helper.setCookie(PRICE_ORDER_INFO_COOKIE_KEY, (this.buyInfoCookie + 1).toString(), true);
			}
		}
	}

	public dismissTooltip(): void {
		const isSell = this.offer.getType() === TRADE_TYPE.SELL;
		this.setCookie(isSell);
		this.showInfoText = false;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		combineLatest({
			isBuyEnabled: this.limitOrdersService.isBuyLimitEnabledFF$,
			isSellEnabled: this.limitOrdersService.isSellLimitEnabledFF$,
			price: this.priceSubject$,
			typeVariation: this.switchVariations$
		})
			.pipe(take(1))
			.subscribe(({ isBuyEnabled, isSellEnabled, price, typeVariation }) => {
				const orderType = this.offer.getType();
				const isSell = orderType === TRADE_TYPE.SELL;
				this.setCookieData(isSell);
				this.showInfoText =
					!this.cameFromHeaderSwitch && (isSell ? !this.hasSeenSellTooltip : !this.hasSeenBuyTooltip);
				const asset = this.tradableService.getItemById(this.offer.getCurrencyId());

				this.orderTypeOptions = [
					{
						label: isSell ? "market_order_sell_header_option" : "market_order_buy_header_option",
						icon: "/assets/images/market-order-icon.svg",
						subtext: isSell
							? "market_order_sell_description_option"
							: "market_order_buy_description_option",
						value: "market_order",
						available: true
					},
					{
						label: isSell ? "price_order_sell_header_option" : "price_order_buy_header_option",
						icon: "/assets/images/limit-order-icon.svg",
						subtext: isSell ? "price_order_sell_description_option" : "price_order_buy_description_option",
						value: "price_order",
						available: asset?.getAssetSettings().isPriceOrderAvailable()
					}
				];

				this.extendOptions = this.orderTypeOptions;

				if ((isSell && isSellEnabled) || (!isSell && isBuyEnabled)) {
					this.canShowExtension = true;
				}

				this.isAssetEligibleForLimitOrder = asset && asset?.getAssetSettings().isPriceOrderAvailable();

				//automatically the LO switcher depending on the FF status only if price order is available
				if (this.isAssetEligibleForLimitOrder) {
					this.autoOpenSwitcherHelper(price, typeVariation);
				}
			});

		if (this.orderTypeOptions.length > 0) {
			this.selected = this.orderTypeOptions[this.selectedOption];
		}
	}

	public extendHeader(): void {
		this.priceSubject$.pipe(take(1)).subscribe((price) => {
			if (price) {
				if (!this.canShowExtension) {
					return;
				}
				// Set cookie first time it's clicked
				const isSell = this.offer.getType() === TRADE_TYPE.SELL;
				this.setCookie(isSell);
				this.showInfoText = false;
				this.showExtendOptions = !this.showExtendOptions;
			}
			return;
		});
	}

	public selectOption(option: OrderOption, isLimitReached: boolean): void {
		const tradeType = this.offer.getType();
		this.selected = option;

		if (option.value === "price_order" && isLimitReached) {
			this.limitOrdersService.limitOrdersReachedMessage();
		}

		if (option.value === "price_order" && !isLimitReached) {
			if (tradeType === TRADE_TYPE.BUY) {
				this.orderEntryService.openBuyOrderFlow(this.offer.getCurrencyId(), {
					[LimitOrderQueryParamName.WalletId]: this.offer.getWalletId(),
					[LimitOrderQueryParamName.FiatId]: this.offer.getFiatId(),
					[LimitOrderQueryParamName.FiatWalletId]: this.offer.getFiatWalletId(),
					[LimitOrderQueryParamName.Amount]: this.offer.getAmountFiat()
				});
			} else {
				this.orderEntryService.openSellOrderFlow(
					this.offer.getCurrencyId(),
					{
						[LimitOrderQueryParamName.WalletId]: this.offer.getWalletId(),
						[LimitOrderQueryParamName.FiatId]: this.offer.getFiatId(),
						[LimitOrderQueryParamName.FiatWalletId]: this.offer.getFiatWalletId(),
						[LimitOrderQueryParamName.Amount]: this.offer.getAmountTradable(),
						[LimitOrderQueryParamName.OrderType]: OrderType.Sell
					},
					true
				);
			}
			this.close();
		} else {
			return;
		}

		//update cookie behaviour for the auto switch visibilty
		this.limitOrdersService.navigateAwayAndUpdateSwitchVisilibityCookie(true, this.isAssetEligibleForLimitOrder);
	}

	@HostListener("document:click", ["$event"])
	public closeHeader(event: MouseEvent): void {
		if (!this.elementRef.nativeElement.contains(event.target)) {
			this.showExtendOptions = false;
		}
	}
}
